import { useEffect } from "react";
import i18n from "i18next";
import type { TeamAppProps } from "../host/teamAppZoid";
import { toTeamLocale } from "./presenterLang";

// zoid exposes a child-side subscription on `window.xprops.onProps` —
// invoked with the latest props whenever the parent calls
// `zoidInstance.updateProps(...)`. Cheaper than polling and fires only
// on real changes.
type ZoidXProps = TeamAppProps & {
  onProps?: (
    handler: (props: TeamAppProps) => void,
  ) => { cancel: () => void };
};

function pickLang(props: TeamAppProps | undefined): string | null {
  // Same presenter-code -> team-bundle mapping the initial detector uses
  // (`pt_BR` -> `br`), so a mid-session language SWITCH resolves the right
  // bundle instead of silently falling back to English.
  return toTeamLocale(
    props?.currentLanguage ?? props?.currentUser?.presenterLanguage,
  );
}

// Mirrors the presenter's `setPresenterLang` mutation: fetch the locale
// bundle, register it with i18next, then switch the active language.
// Registering before switching guarantees the new strings are in the
// store by the time react-i18next re-renders.
async function applyLanguage(lng: string): Promise<void> {
  if (lng !== "en" && !i18n.hasResourceBundle(lng, "translation")) {
    const mod = (await import(`./locales/${lng}/team.json`)) as {
      default: Record<string, unknown>;
    };
    i18n.addResourceBundle(lng, "translation", mod.default, true, true);
  }
  await i18n.changeLanguage(lng);
}

// Match `useCurrentUser`'s polling window — zoid usually finishes its
// handshake within tens of ms, but the React tree can mount before
// `window.xprops` is populated.
const POLL_INTERVAL_MS = 50;
const TIMEOUT_MS = 1500;

export function I18nLanguageBridge() {
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sub: { cancel: () => void } | undefined;
    const deadline = Date.now() + TIMEOUT_MS;

    const apply = (props: TeamAppProps) => {
      const next = pickLang(props);
      if (!next || next === i18n.language) return;
      void applyLanguage(next);
    };

    const setup = () => {
      if (cancelled) return;
      const xprops = (window as { xprops?: ZoidXProps }).xprops;
      if (!xprops) {
        if (Date.now() >= deadline) return;
        timer = setTimeout(setup, POLL_INTERVAL_MS);
        return;
      }
      apply(xprops);
      sub = xprops.onProps?.((p: TeamAppProps) => apply(p));
    };
    setup();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      sub?.cancel();
    };
  }, []);
  return null;
}
